import React, { useState } from 'react';

import { LUT_GROUPS, css, lutColor, lutLabel } from '../Viewer/lut';
import { BLOOD_DEFAULTS, BLOOD_FALLBACK } from '../Viewer/blood';
import { CardTitle, LutRange, Slider } from './ChannelCard';

// Fenêtre d'origine d'une copie 8 bits : valeurs d'origine de ses niveaux 0 et 255 (fichier .window.json de la copie).
// Saisie locale, transmise quand les deux valeurs sont valides (haut > bas).
export function WindowRow({ channel, onWindow }) {
  const [values, setValues] = useState(channel.sourceWindow ? channel.sourceWindow.map(String) : ['', '']);
  const set = (i, v) => {
    const next = [...values];
    next[i] = v;
    setValues(next);
    const [lo, hi] = next.map(Number);
    if (next.every(x => x !== '') && hi > lo) onWindow(channel.id, [lo, hi]);
  };
  return (
    <div className="compare-row">
      <span className="compare-label shrink" title="8-bit copy: original values of its levels 0 and 255 (the copy's .window.json file)">
        Window {channel.name}
      </span>
      {[0, 1].map(i => (
        <input key={i} type="number" className="compare-number" value={values[i]} placeholder={i ? 'high' : 'low'}
          aria-label={`${i ? 'High' : 'Low'} end of the original window, ${channel.name}`} onChange={e => set(i, e.target.value)} />
      ))}
    </div>
  );
}

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Canal « Sang » (src/components/Viewer/blood.js), présenté comme une modalité : interrupteur (exclusif), canal
// absorbant (415 nm) et référence (530 ou 625 nm), affichage (carte de chaleur : mailles, lissage, opacité ; ou pixel
// par pixel), k, seuil et maximum affichés, LUT, fond, noir de la caméra.
// glass : niveau du verre de chaque canal, en unités d'origine (estimé sur l'image). Copie 8 bits sans fenêtre
// d'origine connue : la transmission absolue est inconnue (0 de la copie ≠ noir), fenêtre à renseigner (onWindow).
export default function BloodChannelCard({
  channels, s, r, settings, glass, active, error, onChange, onWindow, onToggle, onEstimate, collapsed, onCollapse, label, onRename,
}) {
  const [estimating, setEstimating] = useState(false);
  const estimate = async () => {
    setEstimating(true);
    try { await onEstimate(); } finally { setEstimating(false); }
  };
  const k = settings.k ?? BLOOD_FALLBACK.k;
  const heatmap = settings.display !== 'pixels';
  const levels = s?.loader.length ?? 0;  // niveaux de la pyramide (mailles possibles)
  const odMin = settings.odMin ?? BLOOD_FALLBACK.odMin;
  const odMax = settings.odMax ?? BLOOD_FALLBACK.odMax;
  const select = (key, value, label) => (
    <select value={value?.id || ''} aria-label={label} onChange={e => onChange({ [key]: e.target.value })}>
      {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
    </select>
  );
  const gradient = `linear-gradient(to right, ${Array.from({ length: 17 }, (_, i) => css(lutColor(settings.lut, [255, 255, 255], false, i / 16, settings.lutRange))).join(', ')})`;
  // Fenêtre d'origine d'une copie 8 bits, modifiable si les tuiles ne la donnent pas (16 bits : connue).
  const editableWindow = ch => ch && ch.loader[0].dtype === 'Uint8' && (!ch.toOriginal || !!ch.sourceWindow);
  const unknown = [s, r].filter(ch => ch && !ch.scaleKnown);
  const glassText = [s, r].filter(Boolean)
    .map(ch => `${ch.name} ${glass[ch.id] != null ? Math.round(glass[ch.id]).toLocaleString('en-US') : '…'}`).join(' · ');
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch blood" title="Computed channel: blood" />
        <CardTitle name={label || 'Blood (absorbance)'} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={e => onToggle(e.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            Blood = OD(absorbing) − k × OD(reference), OD = −log10 of the transmission (glass = 1).
          </p>
          <div className="compare-row">
            <span className="compare-label">Display</span>
            <select value={settings.display} aria-label="Blood channel display" onChange={e => onChange({ display: e.target.value })}>
              <option value="heatmap">Heat map (low resolution, over the tissue)</option>
              <option value="pixels">Pixel by pixel (full resolution)</option>
            </select>
          </div>
          <div className="compare-row">
            <span className="compare-label">Absorbing</span>{select('s', s, 'Absorbing channel (blood)')}
          </div>
          <div className="compare-row">
            <span className="compare-label">Reference</span>{select('r', r, 'Reference channel (tissue)')}
          </div>
          {error && <p className="viewer-note error">{error}</p>}
          {[s, r].filter(editableWindow).map(ch => <WindowRow key={ch.id} channel={ch} onWindow={onWindow} />)}
          {unknown.length > 0 && (
            <p className="viewer-note error" style={{ margin: 0 }}>
              {unknown.map(ch => ch.name).join(', ')}: 8-bit copy without its original window, the copy's 0 is not black.
              Enter the window (low – high from the copy's .window.json file), otherwise the absorbance is wrong.
            </p>
          )}
          <Slider label="k (reference scattering scaled to the absorbing channel)" value={k} min={0.5} max={3} step={0.01}
            reset={BLOOD_FALLBACK.k} format={v => `k = ${num(v)}`} onChange={v => onChange({ k: v })} />
          <div className="channel-options">
            <button type="button" className="chip" onClick={estimate} disabled={estimating || !!error}
              title="k = median OD ratio on the tissue (cancels tissue without blood); threshold: median + 3 robust deviations of the signal on the tissue (+ 2 on the smoothed, gridded heat map); maximum: 99.9th percentile">
              {estimating ? 'Computing…' : 'Estimate k and range automatically'}
            </button>
          </div>
          <Slider label={`Threshold (blood OD; below: ${heatmap ? 'transparent' : 'black'})`} value={odMin} min={0}
            max={heatmap ? 0.2 : 1} step={heatmap ? 0.0005 : 0.005}
            reset={BLOOD_FALLBACK.odMin} format={v => `${num(v, heatmap ? 4 : 3)} OD`} onChange={v => onChange({ odMin: v })} />
          <Slider label="Displayed maximum (blood OD)" value={odMax} min={heatmap ? 0.005 : 0.02} max={2}
            step={heatmap ? 0.0005 : 0.01}
            reset={BLOOD_FALLBACK.odMax} format={v => `${num(v, heatmap ? 4 : 2)} OD`} onChange={v => onChange({ odMax: v })} />
          <div className="compare-row">
            <span className="compare-label">LUT</span>
            <select value={settings.lut} aria-label="Blood channel LUT" onChange={e => onChange({ lut: e.target.value })}>
              {LUT_GROUPS.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.ids.map(id => <option key={id} value={id}>{lutLabel(id)}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <LutRange lut={settings.lut} range={settings.lutRange} onChange={r => onChange({ lutRange: r })} />
          <div className="lut-bar" style={{ background: gradient }} />
          <div className="range-values"><span>{num(odMin, heatmap ? 4 : 3)}</span><span>{num(odMax, heatmap ? 4 : 2)} blood OD</span></div>
          {heatmap && (
            <>
              <div className="compare-row">
                <span className="compare-label">Grid</span>
                <select value={settings.heatLevel} aria-label="Heat map grid size"
                  onChange={e => onChange({ heatLevel: Number(e.target.value) })}>
                  {[4, 5, 6, 7, 8].filter(l => l < levels).map(l => (
                    <option key={l} value={l}>{2 ** l} × {2 ** l} px</option>
                  ))}
                </select>
              </div>
              <Slider label="Map smoothing (σ, grid cells)" value={settings.heatSmooth} max={4} step={0.25}
                reset={BLOOD_DEFAULTS.heatSmooth} format={v => (v ? `σ ${v.toLocaleString('en-US')}` : 'none')}
                onChange={v => onChange({ heatSmooth: v })} />
              <Slider label="Map opacity" value={settings.opacity} max={1} reset={BLOOD_DEFAULTS.opacity}
                onChange={v => onChange({ opacity: v })} />
            </>
          )}
          <Slider label="Background (tissue, reference OD, full resolution)" value={settings.context} max={1}
            reset={BLOOD_DEFAULTS.context} onChange={v => onChange({ context: v })} />
          <div className="compare-row">
            <span className="compare-label">Camera black</span>
            <input type="number" className="compare-number" value={settings.dark} step="1" aria-label="Camera black"
              title="Value of a pixel without light, in original units (0 if unknown)"
              onChange={e => onChange({ dark: Number(e.target.value) || 0 })} />
          </div>
          <p className="viewer-note" style={{ margin: 0 }}>Glass (estimated background, original units): {glassText}</p>
        </div>
      )}
    </div>
  );
}
